import { L } from '@lib';
import { Customer } from '@services';
import { useStore } from '@stores';
import dayjs from 'dayjs';
import { Modal, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { NotifyChannelsSelect, availableNotifyChannels, defaultNotifyChannels } from '../Select/NotifyChannelsSelect';

const { Text } = Typography;

type SmsKind = 'estimate' | 'booking' | 'details';

interface SmsValues {
  customer?: string;
  registration?: string;
  garage?: string;
  phone?: string;
  dropOff?: string | Date | null;
}

/**
 * The text messages the server sends (OrderCustomerNotifier), word for word, filled in with what is known - the
 * same wording and the same UK date format. What the customer gets by SMS is only this short message, never the
 * estimate or the booking itself. What is not known yet stays a [placeholder].
 */
const smsText = (kind: SmsKind, v: SmsValues) => {
  const name = (v.customer ?? '').split(' ').filter(Boolean)[0] ?? 'there';
  const reg = v.registration || '[Registration]';
  const garage = v.garage || '[Garage Name]';
  const callUs = v.phone ? ` Questions? Call ${v.phone}.` : '';
  const when = v.dropOff ? dayjs(v.dropOff).tz('Europe/London') : null;
  const date = when ? when.format('DD/MM/YY') : '[dd/mm/yy]';
  const time = when ? when.format('HH:mm') : '[HH:mm]';
  if (kind === 'estimate') return `Hi ${name}, your estimate for ${reg} from ${garage} is ready to review.${callUs}`;
  if (kind === 'details')
    return `Hi ${name}, here are the details of your booking for ${reg} at ${garage}: drop off on ${date} at ${time}.${callUs}`;
  return `Hi ${name}, your booking for ${reg} at ${garage} is confirmed for ${date} at ${time}.${callUs}`;
};

interface IProps {
  open: boolean;
  customer?: Pick<Customer, 'userId' | 'email' | 'phone' | 'fullname'> | null;
  title: string;
  /** What will happen, in a sentence or two. */
  description: string;
  /** The line above the channels, e.g. "Send estimate by". */
  label: string;
  okText: string;
  /**
   * Telling nobody is a fair answer here (the confirmation of a new order: the customer may not be told yet).
   * Then the button is never held, and with no channel picked it reads `noneOkText`.
   */
  allowNone?: boolean;
  noneOkText?: string;
  /** Which text message goes out if SMS is picked (see SMS_SAMPLE); it is shown so nobody expects more than that. */
  sms: SmsKind;
  /** The vehicle's plate and the drop-off, for the sample text. */
  registration?: string;
  dropOff?: string | Date | null;
  /** Does the telling with the chosen channels; the modal stays open until it is done, and closes if it says yes. */
  onConfirm: (channels: string[]) => Promise<boolean>;
  onCancel: () => void;
}

/**
 * The one modal for telling a customer something now - a new estimate, the details of a booking: what will
 * be sent, and App / Email / SMS to choose how. The choice starts as the booking drawer's own default
 * (the channels the customer can be reached on, app and email first), and the button is held while none is
 * picked, since there would be nothing to send.
 */
export const NotifyCustomerModal: React.FC<IProps> = ({
  open,
  customer,
  title,
  description,
  label,
  okText,
  allowNone,
  noneOkText,
  sms,
  registration,
  dropOff,
  onConfirm,
  onCancel,
}) => {
  const [channels, setChannels] = useState<string[]>(() => defaultNotifyChannels(customer));
  const [sending, setSending] = useState(false);

  // Each time it opens the choice starts again from the default. Only on opening: the customer object is
  // replaced whenever the job is refreshed, which must not undo what has been picked in the meantime.
  const customerRef = useRef(customer);
  customerRef.current = customer;
  useEffect(() => {
    if (open) setChannels(defaultNotifyChannels(customerRef.current));
  }, [open]);

  // The garage's name and phone, for the sample text: both come with the login (the session's tenant).
  const { sessionStore } = useStore();
  const tenant = sessionStore.currentLogin?.tenant;

  const available = availableNotifyChannels(customer);
  const pickable = channels.filter((channel) => available[channel as keyof typeof available]);

  const confirm = async () => {
    setSending(true);
    try {
      if (await onConfirm(pickable)) onCancel();
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      title={L(title)}
      okText={L(allowNone && pickable.length === 0 && noneOkText ? noneOkText : okText)}
      okButtonProps={{ disabled: !allowNone && pickable.length === 0 }}
      confirmLoading={sending}
      onOk={confirm}
      onCancel={onCancel}
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {L(description)}
      </Text>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>
        {L(label)}
      </Text>
      {/* Room between the channels and the buttons under them, which sat right against them. */}
      <div style={{ marginBottom: 20 }}>
        <NotifyChannelsSelect customer={customer} value={channels} onChange={setChannels} />
      </div>
      {/* SMS costs credits and carries only a short message: said before it is sent. */}
      {pickable.includes('Sms') && (
        <div
          style={{
            marginTop: -8,
            marginBottom: 8,
            padding: '10px 12px',
            borderRadius: 8,
            background: '#f5f9ff',
            border: '1px solid #dbe7fb',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <Text strong style={{ fontSize: 13 }}>
              {L('SMS message that will be sent')}
            </Text>
            <Text style={{ fontSize: 12, color: '#d46b08' }}>{L('Uses 1 Umoto credit per text message')}</Text>
          </div>
          <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>{smsText(sms, { customer: customer?.fullname, registration, garage: tenant?.name, phone: tenant?.companyPhone, dropOff })}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {L('The SMS is only this short message - the customer does not get the full details in it.')}
          </Text>
        </div>
      )}
    </Modal>
  );
};
